<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";

  .task {
    height: 100%;
    display: flex;
    overflow: auto;
    padding: utils.in_rem(24px) 2px 2px 2px;

    &.normal {
      flex-direction: column;
      gap: utils.in_rem(33px);
    }

    &.compact {
      flex-direction: row;
      gap: utils.in_rem(22px);
    }
  }

  .send-email {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: utils.in_rem(16px);
    overflow: auto;
    padding: utils.in_rem(24px) 2px 2px 2px;

    &.normal {
      flex-direction: column;
      gap: utils.in_rem(16px);
    }

    &.compact {
      flex-direction: row;
      gap: utils.in_rem(22px);
    }
  }

  .bounce {
    height: 100%;
    display: flex;
    overflow: auto;
    padding: utils.in_rem(24px) 2px utils.in_rem(16px) 2px;

    &.normal {
      flex-direction: column;
      gap: utils.in_rem(22px);
    }

    &.compact {
      flex-direction: row;
      gap: utils.in_rem(22px);
      align-items: center;
    }

    .new-email-input {
      display: flex;
      flex-direction: column;
      gap: utils.in_rem(22px);

      &.compact {
        width: 30%;
      }
    }
  }

  .message {
    display: flex;
    flex-direction: column;
    gap: utils.in_rem(20px);

    .headers {
      display: flex;
      flex-direction: column;
      gap: utils.in_rem(0px);
      .inline {
        display: flex;
        gap: utils.in_rem(8px);
        align-items: center;

        > h4 {
          margin: 0;
        }
      }
    }
  }
</style>

<script>
  import DOMPurify from "dompurify";

  import Container from "@sourcewhale/shared/svelte-design-system/components/atoms/Container.svelte";
  import InlineNotification from "@sourcewhale/shared/svelte-design-system/components/atoms/InlineNotification.svelte";
  import StepEditor from "@sourcewhale/shared/svelte-design-system/components/molecules/Editor/StepEditor.svelte";
  import TextInput from "@sourcewhale/shared/svelte-design-system/components/molecules/InputFields/TextInput.svelte";
  import AiTaskCard from "../components/AiTaskCard.svelte";
  import AlternativeEmailsContainer from "../components/AlternativeEmailsContainer.svelte";
  import ManualTaskCard from "../components/ManualTaskCard.svelte";
  import { compactModal, todoPanelStore, todosModalItemStore } from "../stores";

  const headings = {
    generalTask: "General Task Step",
    phoneCall: "Phone Call Step",
  };

  const labels = {
    generalTask: "General Task Notes",
    phoneCall: "Phone Call Notes",
  };

  const placeholders = {
    generalTask: "Start typing your general task notes here",
    phoneCall: "Start typing your phone call notes here",
  };

  const toolbar = [
    `customVariablesBtn fontselect fontsizeselect | bold italic underline forecolor | align numlist bullist table directionalityBtn`,
    `attachmentsBtn link imageGifBtn videoBtn emojiBtn | customButton`,
  ];

  $: sizeCls = $compactModal ? "compact" : "normal";
</script>

{#if $todosModalItemStore?.kind === "sendEmailAfterInmail"}
  <div class="send-email normal">
    <InlineNotification
      message="information"
      size="large"
      contrast="high"
      class="align-self-start"
    >
      This email was scheduled after a manual step. Please ensure that it is
      appropriate to send.
    </InlineNotification>
    <h3>Email step</h3>
    <StepEditor
      bind:messageText={$todoPanelStore.messageText}
      bind:subject={$todoPanelStore.subject}
      bind:attachmentIds={$todoPanelStore.attachmentIds}
      {toolbar}
    />
  </div>
{:else if $todosModalItemStore?.kind === "bounce"}
  <div class="bounce {sizeCls}">
    <div class="new-email-input {sizeCls}">
      <InlineNotification
        message="information"
        size="large"
        contrast="high"
        class="align-self-start"
      >
        This email was not delivered. Add a new email address or convert to an
        InMail step.
      </InlineNotification>
      <TextInput
        style={$compactModal ? "" : "width:60%"}
        label="Add New Email Address"
        kind="input"
        bind:value={$todoPanelStore.newEmail}
        placeholder="Enter Email Address"
        labelSize="small"
        helperText={$todoPanelStore.invalidEmail ? "Invalid email address" : ""}
        state={$todoPanelStore.invalidEmail ? "error" : ""}
      />
    </div>
    <AlternativeEmailsContainer />
  </div>
{:else if ["generalTask", "phoneCall"].includes($todosModalItemStore?.kind)}
  <div class="task {sizeCls}">
    <Container
      heading={headings[$todosModalItemStore?.kind]}
      style={$compactModal ? "min-height:11rem" : "height:50%"}
      class="flex-1"
    >
      <!-- eslint-disable-next-line svelte/no-at-html-tags -->
      {@html DOMPurify.sanitize($todosModalItemStore.data.messageText)}
    </Container>
    {#if $todosModalItemStore?.kind === "phoneCall"}
      <TextInput
        style={$compactModal ? "min-height:11rem" : "height:50%;width:100%"}
        class="flex-1"
        label={labels[$todosModalItemStore?.kind]}
        kind="textarea"
        bind:value={$todoPanelStore.phoneCallNote}
        placeholder={placeholders[$todosModalItemStore?.kind]}
        labelSize="large"
      />
      <!-- TODO add this notes box to general task and push them to relevant CRMs
     (requires backend change, as currently general tasks do not have notes) -->
    {/if}
  </div>
{:else if $todosModalItemStore?.kind === "aiToDo"}
  <AiTaskCard />
{:else if $todosModalItemStore?.kind === "manualToDo"}
  <ManualTaskCard />
{/if}
